import { useAppSelector } from "../../../../../store/hooks";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Pages } from "../../../../../store/reducers/builderReducers/pages/pagesApi";
import { getPageLocaleUrl } from "../../../../_default/utilities/helpers";
import {
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "../../../../../utils/languageCode";

type PageUrlAttributeKey =
  | "terms_url"
  | "cookie_policy_url"
  | "privacy_policy_url";

interface Props {
  componentId: number;
  attributeKey: PageUrlAttributeKey;
  label: string;
  savedUrl: string | null | undefined;
  triggerResize: () => void;
}

interface PageOption {
  page: Pages["list"][number];
  localeUrl: string;
}

function looksLikeCustomUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return (
    trimmed.startsWith("/") ||
    /^https?:\/\//i.test(trimmed) ||
    trimmed.includes("://")
  );
}

function normalizeUrlValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("/") || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (/^[^\s/]+\.[^\s]+$/i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

export default function CheckoutPageUrlInput({
  componentId,
  attributeKey,
  label,
  savedUrl,
  triggerResize,
}: Props) {
  const webpages = useAppSelector(
    (state) => state.builder.pages.list as Pages["list"]
  );
  const languages = useAppSelector((state) => state.builder.languageSettings);

  const [searchQuery, setSearchQuery] = useState(savedUrl ?? "");
  const [filteredPages, setFilteredPages] = useState<PageOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectingRef = useRef(false);
  const skipNextBlurCommitRef = useRef(false);
  const searchQueryRef = useRef(searchQuery);

  searchQueryRef.current = searchQuery;

  const pageOptions = useMemo(() => {
    return webpages.map((page) => ({
      page,
      localeUrl: getPageLocaleUrl(page, languages.activeLanguages),
    }));
  }, [webpages, languages.activeLanguages]);

  useEffect(() => {
    setSearchQuery(savedUrl ?? "");
  }, [savedUrl]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const filterPages = useCallback(
    (query: string) => {
      const normalized = query.trim().toLowerCase();
      if (!normalized) {
        setFilteredPages(pageOptions);
        return;
      }
      setFilteredPages(
        pageOptions.filter(
          ({ page, localeUrl }) =>
            page.name.toLowerCase().includes(normalized) ||
            page.url.toLowerCase().includes(normalized) ||
            localeUrl.toLowerCase().includes(normalized)
        )
      );
    },
    [pageOptions]
  );

  const persistUrl = useCallback(
    (url: string | null) => {
      updateStructureComponent(componentId, `attributes.${attributeKey}`, url);
      triggerResize();
    },
    [attributeKey, componentId, triggerResize]
  );

  const openPageSearch = useCallback(() => {
    setIsSearching(true);
    filterPages(searchQuery);
    searchRef.current?.focus();
    triggerResize();
  }, [filterPages, searchQuery, triggerResize]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (looksLikeCustomUrl(value)) {
      setIsSearching(false);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      triggerResize();
      return;
    }
    if (!isSearching) {
      return;
    }
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      filterPages(value);
    }, 300);
  };

  const handleSearchFocus = () => {
    triggerResize();
  };

  const commitSearchQuery = useCallback(() => {
    const normalized = normalizeUrlValue(searchQueryRef.current);
    const nextUrl = normalized.length > 0 ? normalized : null;
    const currentUrl =
      typeof savedUrl === "string" && savedUrl.trim().length > 0
        ? savedUrl.trim()
        : null;
    if (nextUrl !== currentUrl) {
      persistUrl(nextUrl);
    }
  }, [persistUrl, savedUrl]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsSearching(false);
      triggerResize();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      commitSearchQuery();
      setIsSearching(false);
      skipNextBlurCommitRef.current = true;
      searchRef.current?.blur();
    }
  };

  const handleSearchBlur = () => {
    setIsSearching(false);
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      triggerResize();
      return;
    }
    if (!selectingRef.current) {
      commitSearchQuery();
    }
    selectingRef.current = false;
    triggerResize();
  };

  const handleSelectPage = (option: PageOption) => {
    selectingRef.current = true;
    setSearchQuery(option.localeUrl);
    setIsSearching(false);
    persistUrl(option.localeUrl);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchQuery("");
    setIsSearching(false);
    setFilteredPages([]);
    persistUrl(null);
  };

  const groupedFilteredPages = useMemo(() => {
    const groups: { languageCode: string; options: PageOption[] }[] = [];
    languages.activeLanguages.forEach((language) => {
      const langCode = languageCodeFromActiveLanguage(language);
      const options = filteredPages
        .filter(({ page }) =>
          languageCodesEqual(page.languageCode, langCode)
        )
        .sort((a, b) => a.page.name.localeCompare(b.page.name));
      if (options.length > 0) {
        groups.push({ languageCode: langCode, options });
      }
    });
    return groups;
  }, [filteredPages, languages.activeLanguages]);

  const hasSavedUrl =
    typeof savedUrl === "string" && savedUrl.trim().length > 0;

  return (
    <div
      className="d-flex flex-wrap align-items-center"
      style={{ marginTop: "3px" }}
    >
      <div style={{ marginRight: "5px", minWidth: "120px" }}>{label}</div>
      <div
        className="content-width-menu_input position-relative"
        style={{ width: "280px" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            borderRadius: "4px",
            padding: "2px 4px",
            width: "100%",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            searchRef.current?.focus();
          }}
        >
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            onKeyDown={handleSearchKeyDown}
            placeholder="Enter URL or click search for pages..."
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              width: "100%",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />
          <SearchIcon
            titleAccess="Search pages"
            style={{
              fontSize: "16px",
              marginLeft: "4px",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              openPageSearch();
            }}
          />
          {hasSavedUrl && (
            <ClearIcon
              style={{
                fontSize: "16px",
                marginLeft: "4px",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={handleClear}
            />
          )}
        </div>

        {isSearching && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              maxHeight: "200px",
              overflowY: "auto",
              top: "24px",
              background: "#323232",
              borderRadius: "2px",
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {groupedFilteredPages.length > 0 ? (
              groupedFilteredPages.map((group) => (
                <React.Fragment key={group.languageCode}>
                  <div
                    className="dropdown-item builder-dropdown__item"
                    style={{
                      fontWeight: 600,
                      opacity: 0.7,
                      pointerEvents: "none",
                    }}
                  >
                    {group.languageCode}
                  </div>
                  {group.options.map((option) => (
                    <div
                      key={option.page.uuid}
                      className={
                        "dropdown-item builder-dropdown__item" +
                        (savedUrl === option.localeUrl
                          ? " builder-dropdown__item--active"
                          : "")
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectPage(option);
                      }}
                    >
                      {option.page.name}
                    </div>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <div className="dropdown-item builder-dropdown__item">
                No pages found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
